import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, makeStyles, Link, Box, Typography,
  Dialog, DialogContent, DialogActions, Button, IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import { PLANNED_CC_DATA, CHANGE_CONTROL_DATA, mergeTabData, selectTabData } from './manufacturingTabData';
import { ccDoc, openDoc } from './mfgDocLinks';
import Pagination from '../../logisticsDashboard/components/Pagination';


const fmtTimestamp = (v) => {
  if (!v) return null;
  const m = moment(v);
  if (!m.isValid()) return null;
  return { time: m.format('HH:mm:ss'), date: m.format('DD-MMM-YYYY').toUpperCase() };
};

const useStyles = makeStyles(() => ({
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.88rem',
      color: '#333',
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'bottom',
      lineHeight: 1.3,
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.88rem',
      padding: '8px 10px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      verticalAlign: 'top',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  blueLink: {
    color: '#1976d2',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: 400,
    textDecoration: 'none',
    display: 'block',
    '&:hover': { textDecoration: 'underline' },
  },
  timestamp: {
    fontSize: '0.72rem',
    color: '#888',
    display: 'block',
    marginTop: '2px',
  },
  // Stacked status + ID with a horizontal divider between them (matches the
  // Deviation tab). The last value drops the divider.
  stackedValue: {
    fontSize: '0.88rem',
    color: '#444',
    paddingBottom: '4px',
    marginBottom: '4px',
    borderBottom: '1px solid #e8e8e8',
    display: 'block',
    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  readMore: {
    color: '#1976d2',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: 400,
  },
}));

const TRUNCATE = 45;

const TruncatedText = ({ text, label, classes }) => {
  const [open, setOpen] = useState(false);
  if (text.length <= TRUNCATE) return <span>{text}</span>;
  return (
    <>
      <span>
        {text.substring(0, TRUNCATE)}...
        <span className={classes.readMore} onClick={() => setOpen(true)}>
          read more
        </span>
      </span>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '12px 16px 0' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{label || 'Details'}</Typography>
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent>
          <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#444' }}>{text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary" size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const STATIC_CC_TYPES = ['Stage', 'Stage', 'Stage', 'Planned', 'Planned'];

// Static demo CC IDs now shown stacked UNDER the status columns (replacing the
// removed separate Sponsor/CMO CC Number columns). An empty Sponsor CC ID means
// no sponsor CC has been generated yet → the "Generate CC" button stays enabled.
const STATIC_CMO_CC_IDS = ['CMO-CC-2026-020', 'CMO-CC-2026-021', 'CMO-CC-2026-022', 'CMO-PCC-2026-101', 'CMO-PCC-2026-102'];
const STATIC_SPONSOR_CC_IDS = ['', 'SCC-2026-002', '', 'SCC-2026-101', ''];

// Static UI text for first row — these columns don't come from the API
const FIRST_ROW_CHANGE_FROM = `Paracetamol Injection Stage C approved Batch Production Record is available with following details:
1) At Operation number 39 filter cloth usage instruction available as "use 1.0 – 5.0 micron PP/Nylon filter cloth".
2) At operation number 44, 45, 48, 49, 51, 52, 56, 60, 61 and 64 filtration followed by washing of the wet material operational instructions available.
3) At operation number 51, 52, 60 and 61 slurry wash to the wet material operational instructions available in single lot provision.
4) At operation number 68 recommended drying time available as "2-3 hours".`;

const FIRST_ROW_CHANGE_TO = `At Operation number 39 filter cloth usage instruction to be changed as "use 1.0 micron PP filter cloth".
2) At operation number 44, 45, 48, 49, 50, 51, 52, 56, 60, 61 and 64 filtration followed by washing of the wet material operational instructions to be elaborated as "If product is observed in MLs, inform to PE/PR&D for further recommendation and based on recommendation, collect it in clean and dry container / ML catch pot/collection receiver and recirculate the obtained filter ML's through ANFD until clear filter ML's were obtained and then continue filtration. If recirculation is to be performed, ensure the containers (Used for filter ml's collection) are clean and dry prior to recirculation".
3) At operation number 51, 52, 60 and 61 slurry wash to the wet material operational instructions to be provided in three lot provision.
4) At operation number 68 Recommended drying time to be changed as "4-5 hours".
5) At operation number 2 instruction to be incorporated as "Ensure ANFD ML catch pot is clean, dry and free from previous product. Note: Record the catch pot cleaning details in Equipment Cleaning Record table".`;

const FIRST_ROW_REASONS = `Based on previous batch experience the material is in fine nature so recommended to fix the 1 micron PP filter cloth.
2) Based on previous batch learning (Ref. Batch number: 400681), material passage was observed during filtration so as a precautionary measure operational instruction elaborated to reduce the material loss in the filter MLs.
3) Total slurry wash volume is higher than the overall holding capacity of ANFD (including wet material) hence, to accommodate the bigger batch size proposed to split the slurry wash volume in three equal lot and perform the ANFD material slurry washes in lot wise (three lots).
4) Recommended drying time increased to reduce lumps formation.
5) Proposed to perform recirculation of the filter MLs into ANFD through catch pot hence, to ensure cleanliness of the catch pot operation was incorporated.`;

const DEMO_PO = '1150345';

// The table shows the batch's unplanned "Change Control" sheet rows (reactive
// CAPAs, ccType "Stage") first, then the "Planned Change Control" rows. Planned
// rows take the API value where present, otherwise the Excel fallback (see
// mergeTabData). Unplanned rows are Excel-only and exist only for batches whose
// Change Control sheet is populated (currently TZB2401-DS); 2388/2785 are empty.
// Any other batch's fallback carries its own batch number.
const ChangeControlTab = ({ data, loading, rowData, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const unplannedRows = selectTabData(CHANGE_CONTROL_DATA, rowData?.batchNumber);
  const plannedRows = mergeTabData(data, PLANNED_CC_DATA, rowData?.batchNumber);
  const rows = [...unplannedRows, ...plannedRows];
  const isDemoPO = rowData?.poNumber === DEMO_PO || rowData?.scheduleAgreementNumber === DEMO_PO;
  // "Generate CC" is a sponsor-side action (Sponsor -> CMO); hidden for the CMO.
  const isSponsor = localStorage.getItem('collabType') !== 'CMO';

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Paper className={classes.tableContainer} elevation={0}>
      <div className={classes.scrollArea}>
        <Table stickyHeader size="small" style={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell>CC Type</TableCell>
              <TableCell>Initial Document<br />Number</TableCell>
              <TableCell>Final Document<br />Number</TableCell>
              <TableCell style={{ minWidth: '130px' }}>Change From</TableCell>
              <TableCell style={{ minWidth: '130px' }}>Change To</TableCell>
              <TableCell style={{ minWidth: '140px' }}>Reasons For Change</TableCell>
              <TableCell style={{ minWidth: '160px' }}>CC Closure Report</TableCell>
              <TableCell>CMO<br />CC Status<br />CC ID</TableCell>
              {isSponsor && <TableCell style={{ minWidth: '120px' }}>Generate<br />CC</TableCell>}
              <TableCell>Sponsor<br />CC Status<br />CC ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={isSponsor ? 10 : 9} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
            ) : pageRows.map((row, i) => {
              const idx = (safePage - 1) * PAGE_SIZE + i;
              const cmoCCId = row.cmoCCNumber || STATIC_CMO_CC_IDS[idx] || '';
              const sponsorCCId = row.sponsorCCNumber || STATIC_SPONSOR_CC_IDS[idx] || '';
              const hasSponsorCC = !!sponsorCCId; // sponsor CC already generated → disable button
              return (
              <TableRow key={idx} className={classes.bodyRow}>
                <TableCell>{row.ccType || STATIC_CC_TYPES[idx] || 'Stage'}</TableCell>
                <TableCell>{row.initialDocNumber}</TableCell>
                <TableCell>{row.finalDocNumber}</TableCell>
                <TableCell style={{ maxWidth: '140px' }}>
                  <TruncatedText text={(isDemoPO && idx === 0) ? FIRST_ROW_CHANGE_FROM : (row.changeFrom || '')} label="Change From" classes={classes} />
                </TableCell>
                <TableCell style={{ maxWidth: '140px' }}>
                  <TruncatedText text={(isDemoPO && idx === 0) ? FIRST_ROW_CHANGE_TO : (row.changeTo || '')} label="Change To" classes={classes} />
                </TableCell>
                <TableCell style={{ maxWidth: '150px' }}>
                  <TruncatedText text={(isDemoPO && idx === 0) ? FIRST_ROW_REASONS : (row.reasonsForChange || '')} label="Reasons For Change" classes={classes} />
                </TableCell>
                <TableCell style={{ maxWidth: '170px' }}>
                  <Link className={classes.blueLink} style={{ cursor: 'pointer' }} onClick={() => openDoc(ccDoc(row.sponsorCCNumber))}>
                    {row.ccClosureReport?.name || (cmoCCId ? `${cmoCCId}.Change Control Closer report.pdf` : "CMO-CC-2026-020.Change Control Closer report.pdf")}
                  </Link>
                  {(() => {
                    const ts = fmtTimestamp(row.ccClosureReport?.timestamp);
                    return ts ? (
                      <>
                        <span className={classes.timestamp}>Timestamp: {ts.time}</span>
                        <span className={classes.timestamp}>{ts.date}</span>
                      </>
                    ) : null;
                  })()}
                </TableCell>
                {/* CMO CC Status + CC ID stacked with a divider line between them */}
                <TableCell>
                  <span className={classes.stackedValue}>{row.cmoCCStatus || "Closed"}</span>
                  {cmoCCId ? <span className={classes.stackedValue}>{cmoCCId}</span> : null}
                </TableCell>
                {/* Generate CC — sponsor only. Alternates enabled/disabled by row
                    (1st on, 2nd off, ...). Static demo: enabled click does nothing. */}
                {isSponsor && (
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={idx % 2 !== 0}
                      onClick={() => {}}
                      style={{ borderRadius: 20, textTransform: 'none', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '2px 12px' }}
                    >
                      Generate CC
                    </Button>
                  </TableCell>
                )}
                {/* Sponsor CC Status + CC ID stacked with a divider line, or "-" when not yet generated */}
                <TableCell>
                  {hasSponsorCC ? (
                    <>
                      <span className={classes.stackedValue}>{row.sponsorCCStatus || "Closed"}</span>
                      <span className={classes.stackedValue}>{sponsorCCId}</span>
                    </>
                  ) : '-'}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {rows.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}
    </Paper>
  );
};

export default ChangeControlTab;
